import type { Bid, BidStatus } from "@/lib/schemas"
import { bidLifecycleOf, isOpenLifecycle, type LifecycleSource } from "@/lib/bid-lifecycle"

const DAY_MS = 1000 * 60 * 60 * 24

/**
 * Bid statuses where the submission deadline is still actionable
 * (pre-submission). Once a bid is `submitted` — or terminal (`won`/`lost`/
 * `no_go`) — its deadline is historical, so it should never render as
 * "overdue" / urgent red.
 */
export const ACTIVE_BID_STATUSES: readonly BidStatus[] = ["lead", "qualifying", "drafting"]

export function isActiveBidStatus(status: BidStatus): boolean {
  return ACTIVE_BID_STATUSES.includes(status)
}

/** Bids still in play: a lead or an active bid (R4 lifecycle), and not archived. */
export function countActiveBids(bids: readonly (LifecycleSource & Pick<Bid, "archived">)[]): number {
  return bids.filter((bid) => !bid.archived && isOpenLifecycle(bidLifecycleOf(bid).lifecycle)).length
}

/**
 * Whole-day difference between a deadline and `now`, normalized to calendar
 * days (both clamped to midnight) so a same-day deadline is `0` rather than a
 * negative fraction. A positive result is days remaining; negative is days
 * past. Returns `null` for a missing or unparseable date.
 */
export function daysUntilDeadline(
  deadline: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!deadline) return null
  const target = new Date(deadline)
  if (Number.isNaN(target.getTime())) return null
  const t = new Date(target)
  t.setHours(0, 0, 0, 0)
  const n = new Date(now)
  n.setHours(0, 0, 0, 0)
  return Math.round((t.getTime() - n.getTime()) / DAY_MS)
}

function parseAbsoluteDate(dateIso: string | null | undefined): Date | null {
  if (!dateIso) return null
  const d = new Date(dateIso)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * The extracted submission deadline that should be offered as the bid's own
 * deadline, or `null` when there is nothing worth offering.
 *
 * The extracted closing date and the bid record are independent: the analysis
 * never writes the bid deadline, so a bid can show a closing date on its
 * Overview while every deadline-driven surface (sorting, overdue styling, My
 * Work, notifications) still sees nothing. This is the shared predicate for
 * "these two disagree" — used both by the Key Dates table and by the Overview
 * deadline card, so the affordance appears in the same conditions in both.
 *
 * Offered when the extracted date is absolute and parseable AND the bid either
 * has no deadline, has an unparseable one, or has one more than a day away.
 * A sub-day difference is treated as agreement — the extracted value often
 * carries a time of day the hand-typed bid deadline does not.
 */
export function extractedDeadlineToApply(
  extractedDateIso: string | null | undefined,
  currentBidDeadline: string | null | undefined,
): Date | null {
  const extracted = parseAbsoluteDate(extractedDateIso)
  if (!extracted) return null
  const current = parseAbsoluteDate(currentBidDeadline)
  if (!current) return extracted
  return Math.abs(extracted.getTime() - current.getTime()) > DAY_MS ? extracted : null
}

// ===================
// Deadline times (bids.deadline_has_time, migration 183)
// ===================

/**
 * A bid's deadline and whether it carries a time of day. `deadlineHasTime`
 * null or absent means date-only: bids saved before times existed, and the
 * dates copied from a tender's key dates.
 */
export interface DeadlineFields {
  deadline?: string | null | undefined
  deadlineHasTime?: boolean | null | undefined
}

/**
 * Has the deadline passed at `now`?
 * - With a time: from that exact moment.
 * - Date-only: once its calendar day has ended (`daysUntilDeadline < 0`) —
 *   unchanged from before times existed.
 * False for a missing or unparseable deadline.
 */
export function isDeadlinePast(fields: DeadlineFields, now: Date = new Date()): boolean {
  if (fields.deadlineHasTime === true) {
    const at = parseAbsoluteDate(fields.deadline)
    return at !== null && now.getTime() >= at.getTime()
  }
  const days = daysUntilDeadline(fields.deadline, now)
  return days !== null && days < 0
}

/**
 * A bid is "overdue" only while it is still in an active (pre-submission)
 * status and its deadline has passed (see `isDeadlinePast`). A date-only
 * deadline due *today* is not overdue; one with a time is, from that time. A
 * submitted/won/lost/no_go bid is never overdue.
 */
export function isBidOverdue(
  bid: DeadlineFields & { status: BidStatus },
  now: Date = new Date(),
): boolean {
  if (!isActiveBidStatus(bid.status)) return false
  return isDeadlinePast(bid, now)
}

/**
 * The short name of the viewer's time zone at `at`, e.g. "AEST" / "AEDT".
 * `en-AU` so Australian zones get their names (other locales print
 * "GMT+10"); zones without an English abbreviation come back as "GMT+1".
 */
export function viewerTimeZoneName(at: Date = new Date()): string {
  return (
    new Intl.DateTimeFormat("en-AU", { timeZoneName: "short" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value ?? ""
  )
}

/**
 * The zone a time entered for `date` ("YYYY-MM-DD") falls in, for the dialogs'
 * hint: AEDT for a December close even when it is entered in June. Today's
 * zone before a date is chosen.
 */
export function inputTimeZoneName(date?: string): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? "")
  return viewerTimeZoneName(
    day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), 12) : new Date(),
  )
}

/**
 * The closing time in the viewer's own time zone, e.g. "2:00 pm AEST", or null
 * for a date-only, missing or unparseable deadline. Hours and minutes are built
 * by hand, like `absoluteTime`, because ICU's spacing before "pm" varies.
 */
export function deadlineTimeLabel(fields: DeadlineFields): string | null {
  if (fields.deadlineHasTime !== true) return null
  const at = parseAbsoluteDate(fields.deadline)
  if (!at) return null
  const hours24 = at.getHours()
  const meridiem = hours24 < 12 ? "am" : "pm"
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
  const minutes = String(at.getMinutes()).padStart(2, "0")
  const zone = viewerTimeZoneName(at)
  return `${hours12}:${minutes} ${meridiem}${zone ? ` ${zone}` : ""}`
}

/** `dateLabel`, plus ", 2:00 pm AEST" when the deadline carries a time. */
export function withDeadlineTime(dateLabel: string, fields: DeadlineFields): string {
  const time = deadlineTimeLabel(fields)
  return time ? `${dateLabel}, ${time}` : dateLabel
}

function pad2(n: number): string {
  return String(n).padStart(2, "0")
}

/**
 * The values for the deadline's date and time inputs.
 * - With a time: the viewer's own date and time of that instant.
 * - Date-only: the stored date. It is saved as UTC midnight of that date
 *   (`deadlineFromInputValues`), so its UTC date IS the date; reading it in
 *   local time would move it a day for anyone west of UTC. Time stays empty.
 */
export function deadlineInputValues(fields: DeadlineFields): { date: string; time: string } {
  const at = parseAbsoluteDate(fields.deadline)
  if (!at) return { date: "", time: "" }
  if (fields.deadlineHasTime === true) {
    return {
      date: `${at.getFullYear()}-${pad2(at.getMonth() + 1)}-${pad2(at.getDate())}`,
      time: `${pad2(at.getHours())}:${pad2(at.getMinutes())}`,
    }
  }
  return { date: at.toISOString().slice(0, 10), time: "" }
}

/**
 * What to save for a date input and an optional time input, or null with no
 * (valid) date.
 * - A date alone is stored exactly as before times existed — UTC midnight of
 *   the date — and flagged date-only.
 * - A date and time is that moment in the person's own time zone.
 */
export function deadlineFromInputValues(
  date: string | undefined,
  time: string | undefined,
): { deadline: string; deadlineHasTime: boolean } | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? "")
  if (!day) return null
  const clock = /^(\d{2}):(\d{2})/.exec(time ?? "")
  const at = clock
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2]))
    : new Date(date ?? "")
  if (Number.isNaN(at.getTime())) return null
  return { deadline: at.toISOString(), deadlineHasTime: clock !== null }
}

/**
 * A Bids filter date ("2026-10-15") as an instant bounding the viewer's own
 * day: its first moment for `from`, its last for `to`, so the range is
 * inclusive of both whole days. Compared as bare dates (UTC midnight), a
 * 2:00 pm close on the "to" day fell outside the range, and a 10:00 am AEDT
 * close on the 15th — 23:00 UTC on the 14th — landed on the wrong day. A
 * date-only deadline (UTC midnight of its date) falls on the day the app shows
 * it on. Anything that is not a bare date passes through unchanged.
 */
export function deadlineFilterBound(date: string, edge: "from" | "to"): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!day) return date
  const [year, month, dayOfMonth] = [Number(day[1]), Number(day[2]) - 1, Number(day[3])]
  if (edge === "from") return new Date(year, month, dayOfMonth).toISOString()
  return new Date(new Date(year, month, dayOfMonth + 1).getTime() - 1).toISOString()
}

export type DeadlineCountdownTone = "overdue" | "soon" | "normal"

export interface DeadlineCountdown {
  /** Short badge form: "12d left", "Due today", "3d overdue", "Overdue". */
  short: string
  /** Sentence form for running text: "closes in 12 days", "closed 3 days ago". */
  phrase: string
  tone: DeadlineCountdownTone
}

/**
 * Close countdown for a bid. Null once the bid is past submission (its
 * deadline is history then) or when it has no usable deadline.
 */
export function deadlineCountdown(
  fields: DeadlineFields,
  status: BidStatus,
  now: Date = new Date(),
): DeadlineCountdown | null {
  if (!fields.deadline || !isActiveBidStatus(status)) return null
  const days = daysUntilDeadline(fields.deadline, now)
  if (days === null) return null

  const plural = (n: number) => `${n} ${n === 1 ? "day" : "days"}`
  if (days < 0) {
    return { short: `${Math.abs(days)}d overdue`, phrase: `closed ${plural(Math.abs(days))} ago`, tone: "overdue" }
  }
  // A closing time that has passed today: overdue, though no day has gone by.
  if (isDeadlinePast(fields, now)) return { short: "Overdue", phrase: "closed today", tone: "overdue" }
  if (days === 0) return { short: "Due today", phrase: "closes today", tone: "overdue" }
  if (days === 1) return { short: "1d left", phrase: "closes tomorrow", tone: "soon" }
  return { short: `${days}d left`, phrase: `closes in ${plural(days)}`, tone: days <= 7 ? "soon" : "normal" }
}
